import React, { FC, useEffect, useRef } from 'react';
import { Box, Divider, Flex } from '@withjoy/joykit';
import { QuickAccess } from './components/QuickAccess';
import { BrandGroupList } from './components/BrandGroupList';
import { BrandListData, useBrandsListController } from './BrandsList.controller';
import { useTranslation } from '@shared/core';
import { StyledBrandGroupContainer, StyledBreadcrumbWrapper, StyledHeader, StyledHeaderContainer, styles } from './BrandsList.styles';
import { SkeletonGroup, SkeletonText } from '@shared/components/Skeleton';
import { pxToRem } from '@withjoy/joykit/theme';
import { range } from 'lodash-es';
import { useCatalogLoggingContext } from '../../Catalog.logging';
import { useTelemetryPageEvent } from '@shared/utils/hooks/useTelemetryPageEvent';
import { Breadcrumbs } from '../../components';
import { useScrollPosition } from '@shared/utils/hooks/useScrollPosition';
import { CategoryFilter } from './components/CategoryFilter';
import { CategoryFilterMobile } from './components/CategoryFilterMobile';
import { Seo } from '@shared/components/Seo';
import BrandCarousel from '@apps/registry/admin/components/BrandCarousel/BrandCarousel';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useShopStorefrontContext } from '../../components/ShopStorefrontProvider';

export type BrandDataType = {
  id: string;
  name: string;
  publicUrlPath: string;
};

export type BrandRecord = Record<string, BrandDataType[]>;

interface BrandGroupProps {
  formattedBrandList: BrandListData[];
  sectionRefs: React.MutableRefObject<Record<string, Element | null>>;
  onMount: React.Dispatch<React.SetStateAction<boolean>>;
}

const BrandGroups: FC<BrandGroupProps> = props => {
  const { formattedBrandList, sectionRefs, onMount } = props;

  useEffect(() => {
    onMount(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledBrandGroupContainer __css={styles.brandGroupContainer}>
      {formattedBrandList.map(data => (
        <BrandGroupList key={data.key} items={data.brands} title={data.key} refElement={el => (sectionRefs.current[data.key] = el)} />
      ))}
    </StyledBrandGroupContainer>
  );
};

export const BrandsList = () => {
  const {
    seoTrans,
    selectedItem,
    formattedBrandList,
    quickAccessItems,
    sectionRefs,
    headerRef,
    handleSelect,
    isLoadingBrandList,
    isMobile,
    setHasBrandGroupMounted,
    selectedCategory,
    quickAccessRef,
    categories,
    selectedFilter,
    onFilterSelect,
    onClearFilter,
    shopByRef
  } = useBrandsListController();
  const { t } = useTranslation('catalogRegistry');
  const { ourBrands, shopBy, filter, clearAll, confirm } = t('brands');
  const { isWeddingStorefront } = useShopStorefrontContext();
  const { value: isPaidAdsFeatureEnabled } = useFeatureValue('paidAdsForShopEnabled', { skip: !isWeddingStorefront });
  const brandCarouselContainerRef = useRef<HTMLDivElement>(null);

  const translations = { filter, shopBy, clearAll, confirm };

  const { trackBrandListsPage } = useCatalogLoggingContext();
  useTelemetryPageEvent(trackBrandListsPage);

  const scrollPosition = useScrollPosition();
  return (
    <Flex flexDirection="column" justifyContent="center">
      <Seo title={seoTrans.shopBrandIndexTitle()} description={seoTrans.shopBrandIndexDescription()} />
      <StyledBreadcrumbWrapper __css={styles.breadcrumbsWrapper(isMobile)}>
        <Breadcrumbs defaultAlign="center" isMobile={isMobile} categoryId={selectedCategory?.id} hideLastBreadcrumb={false} telemetryPageValue="department" />
      </StyledBreadcrumbWrapper>

      <Box
        marginX={{
          _: 6,
          sm2: pxToRem(88)
        }}
      >
        <Box style={{ maxWidth: 'fit-content', margin: '0 auto' }}>
          <SkeletonGroup
            isReady={!isLoadingBrandList}
            paddingTop={{ _: 6, sm2: 0 }}
            display="flex"
            alignItems="center"
            flexDirection="column"
            placeholder={
              <>
                <SkeletonText rows={1} width={pxToRem(250)} skeletonHeight={pxToRem(72)} paddingBottom={8} />
                <SkeletonText rows={1} width={{ _: 'calc(100vw - 48px)', sm2: '700px', md: '1000px' }} skeletonHeight={pxToRem(44)} paddingBottom={{ _: 6, sm2: pxToRem(60) }} />
                <Box width="100%">
                  <SkeletonText rows={1} width={pxToRem(100)} skeletonHeight={pxToRem(26)} paddingTop={pxToRem(30)} paddingBottom={6} />
                  {range(0, 8).map((_, idx) => (
                    <Box key={idx}>
                      <SkeletonText rows={1} width={{ _: pxToRem(200), sm2: pxToRem(400) }} skeletonHeight={pxToRem(26)} paddingY={4} />
                      <Divider />
                    </Box>
                  ))}
                </Box>
              </>
            }
          >
            <StyledHeaderContainer
              __css={styles.headerContainer(
                scrollPosition > 344,
                isMobile,
                brandCarouselContainerRef?.current?.offsetHeight ? brandCarouselContainerRef?.current?.offsetHeight - (isMobile ? 0 : 50) : 0
              )}
              ref={headerRef}
            >
              <StyledHeader
                __css={styles.header}
                typographyVariant={{ _: 'Hed40', sm: 'HedHero72' }}
                marginBottom={isPaidAdsFeatureEnabled ? 0 : { _: 9, sm2: 0 }}
                paddingBottom={isPaidAdsFeatureEnabled ? 0 : { _: 5, sm2: 8 }}
              >
                {ourBrands()}
              </StyledHeader>
              {isPaidAdsFeatureEnabled && (
                <Box width="100%" ref={brandCarouselContainerRef}>
                  <BrandCarousel containerProps={{ marginTop: { _: 9, md: 6 }, marginBottom: { _: 9, md: pxToRem(40) } }} showAllBrandsLink={false} />
                </Box>
              )}
              <Box paddingX={{ md: isPaidAdsFeatureEnabled ? 7 : 0 }} ref={shopByRef}>
                {!isMobile && <CategoryFilter label={`${shopBy()}:`} categories={categories} selectedItem={selectedFilter} handleSelect={onFilterSelect} />}
                <QuickAccess selectedItem={selectedItem} onSelect={handleSelect} list={quickAccessItems} refEl={quickAccessRef} />
              </Box>
            </StyledHeaderContainer>

            <Box width="100%" paddingX={{ md: isPaidAdsFeatureEnabled ? 7 : 0 }}>
              <BrandGroups formattedBrandList={formattedBrandList} sectionRefs={sectionRefs} onMount={setHasBrandGroupMounted} />
            </Box>
          </SkeletonGroup>
        </Box>
      </Box>
      {isMobile && (
        <CategoryFilterMobile translations={translations} categories={categories} selectedItem={selectedFilter} handleSelect={onFilterSelect} handleClear={onClearFilter} />
      )}
    </Flex>
  );
};
